/**
 * Knowledge-check component (FR-D — Sprint 2 step 4 client side).
 *
 * State machine:
 *
 *   idle ──[Start]──▶ attempting ──[Submit]──▶ reviewing ──[Continue]──▶ done
 *
 * - **idle**: shows "Take the knowledge check" CTA.
 * - **attempting**: renders 5 items, one option-radio-group per item.
 *   Each selection POSTs ``/answer`` (last write wins per item, see
 *   the engine docs in ``backend/caitapi/routers/lessons.py``).
 * - **reviewing**: shows the submit response — pass/fail, score, and
 *   per-item rationales. Wrong answers show the candidate's choice
 *   AND the correct one with rationale text.
 * - **done**: parent re-renders, KC is hidden.
 *
 * The component is the only UI surface that ever knows about
 * ``correct``/``rationale`` — and only AFTER submit, never on start
 * or answer responses (which strip both server-side).
 */
import * as React from "react";
import { useCallback, useState } from "react";

import {
  abandonKnowledgeCheck,
  recordAnswer,
  startKnowledgeCheck,
  submitKnowledgeCheck,
  type KCClientItem,
  type SubmitKCResponse,
} from "@/lib/lesson-api";

type KCState =
  | { kind: "idle" }
  | {
      kind: "attempting";
      attemptId: string;
      items: KCClientItem[];
      passingPercent: number;
      answers: Record<string, string>;
      submitting: boolean;
      error: string | null;
      /** FIX-PLAN-2026-06-02 Bug 2.1: backend resumed an existing
       *  in-flight attempt instead of minting a new one. Drives the
       *  "Picking up where you left off" hint + Reset affordance. */
      resumed: boolean;
    }
  | { kind: "reviewing"; response: SubmitKCResponse }
  | { kind: "starting"; error: string | null }
  | { kind: "resetting"; error: string | null };

export interface KnowledgeCheckProps {
  lessonId: string;
  /** Fired when the candidate hits "Continue" after a passing review.
   *  Parent uses this to re-fetch completion-status. */
  onPassed?: () => void;
  /** Fired when the candidate hits "Continue" after a failing review.
   *  Parent typically resets local state so a retake can begin. */
  onFailedContinue?: () => void;

  /** Test seams. */
  startFn?: typeof startKnowledgeCheck;
  answerFn?: typeof recordAnswer;
  submitFn?: typeof submitKnowledgeCheck;
  abandonFn?: typeof abandonKnowledgeCheck;
}

export function KnowledgeCheck({
  lessonId,
  onPassed,
  onFailedContinue,
  startFn = startKnowledgeCheck,
  answerFn = recordAnswer,
  submitFn = submitKnowledgeCheck,
  abandonFn = abandonKnowledgeCheck,
}: KnowledgeCheckProps): React.JSX.Element {
  const [state, setState] = useState<KCState>({ kind: "idle" });

  const onStart = useCallback(async (): Promise<void> => {
    setState({ kind: "starting", error: null });
    try {
      const r = await startFn(lessonId);
      setState({
        kind: "attempting",
        attemptId: r.attempt_id,
        items: r.items,
        passingPercent: r.passing_percent,
        // FIX-PLAN-2026-06-02 Bug 2.1: backend may return an existing
        // in-flight attempt with prior_answers populated. Pre-select
        // those so the candidate sees what they answered before.
        answers: r.prior_answers ?? {},
        submitting: false,
        error: null,
        resumed: r.resumed ?? false,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not start.";
      setState({ kind: "starting", error: msg });
    }
  }, [lessonId, startFn]);

  /** FIX-PLAN-2026-06-02 Bug 2.1: Reset button handler. Asks backend to
   *  abandon the in-flight attempt then immediately re-starts so the
   *  candidate gets fresh pool draws.
   */
  const onReset = useCallback(async (): Promise<void> => {
    setState({ kind: "resetting", error: null });
    try {
      await abandonFn(lessonId);
      const r = await startFn(lessonId);
      setState({
        kind: "attempting",
        attemptId: r.attempt_id,
        items: r.items,
        passingPercent: r.passing_percent,
        answers: r.prior_answers ?? {},
        submitting: false,
        error: null,
        resumed: false,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not reset.";
      setState({ kind: "resetting", error: msg });
    }
  }, [abandonFn, lessonId, startFn]);

  const onAnswer = useCallback(
    async (itemId: string, optionId: string): Promise<void> => {
      // Optimistic local update + fire-and-forget POST. If the POST
      // fails we don't roll back — the candidate's local state is the
      // truth they care about; server-side last-write-wins handles
      // any out-of-order delivery on submit.
      setState((prev) => {
        if (prev.kind !== "attempting") return prev;
        return {
          ...prev,
          answers: { ...prev.answers, [itemId]: optionId },
        };
      });
      try {
        await answerFn(
          (state.kind === "attempting" ? state.attemptId : ""),
          itemId,
          optionId,
        );
      } catch {
        // Ignore — see comment above.
      }
    },
    [answerFn, state],
  );

  const onSubmit = useCallback(async (): Promise<void> => {
    if (state.kind !== "attempting") return;
    setState({ ...state, submitting: true });
    try {
      const r = await submitFn(state.attemptId);
      setState({ kind: "reviewing", response: r });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not submit.";
      setState({ ...state, submitting: false, error: msg });
    }
  }, [state, submitFn]);

  const onContinue = useCallback((): void => {
    if (state.kind !== "reviewing") return;
    if (state.response.passed) {
      onPassed?.();
    } else {
      onFailedContinue?.();
    }
    setState({ kind: "idle" });
  }, [state, onPassed, onFailedContinue]);

  if (state.kind === "idle" || state.kind === "starting" || state.kind === "resetting") {
    return (
      <section className="rounded-lg border border-border bg-white p-6">
        <h2 className="text-lg font-semibold text-foreground">
          Knowledge check
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          5 multiple-choice questions drawn at random from this lesson's
          pool. You need 80% (4 of 5 correct) to complete the lesson.
          You can retake as many times as you want.
        </p>
        {(state.kind === "starting" || state.kind === "resetting") && state.error !== null ? (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {state.error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void onStart();
          }}
          disabled={
            (state.kind === "starting" || state.kind === "resetting") &&
            state.error === null
          }
          className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          {state.kind === "starting" && state.error === null
            ? "Starting…"
            : state.kind === "resetting" && state.error === null
              ? "Resetting…"
              : "Take the knowledge check"}
        </button>
      </section>
    );
  }

  if (state.kind === "attempting") {
    const allAnswered = state.items.every((item) =>
      Object.hasOwn(state.answers, item.id),
    );
    return (
      <section className="rounded-lg border border-border bg-white p-6">
        <h2 className="text-lg font-semibold text-foreground">
          Knowledge check ({Object.keys(state.answers).length}/{state.items.length}{" "}
          answered)
        </h2>
        {state.resumed ? (
          <div
            className="mt-3 flex flex-col gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900 sm:flex-row sm:items-center sm:justify-between"
            role="status"
          >
            <span>
              Picking up where you left off. Your prior answers are
              pre-selected.
            </span>
            <button
              type="button"
              onClick={() => {
                void onReset();
              }}
              className="self-start rounded-md border border-blue-300 bg-white px-3 py-1 text-xs font-semibold text-blue-900 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:self-auto"
            >
              Reset attempt
            </button>
          </div>
        ) : null}
        <ol className="mt-4 space-y-8">
          {state.items.map((item, idx) => (
            <li key={item.id}>
              <p className="text-sm font-semibold text-foreground">
                {idx + 1}. {item.stem}
              </p>
              <fieldset className="mt-3 space-y-2">
                <legend className="sr-only">Options for question {idx + 1}</legend>
                {item.options.map((opt) => {
                  const selected = state.answers[item.id] === opt.id;
                  return (
                    <label
                      key={opt.id}
                      className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 text-sm ${
                        selected
                          ? "border-primary bg-background"
                          : "border-border hover:bg-muted"
                      }`}
                    >
                      <input
                        type="radio"
                        name={item.id}
                        value={opt.id}
                        checked={selected}
                        onChange={() => {
                          void onAnswer(item.id, opt.id);
                        }}
                        className="mt-0.5"
                      />
                      <span className="flex-1">
                        <span className="font-mono text-xs uppercase text-muted-foreground">
                          {opt.id}.
                        </span>{" "}
                        {opt.text}
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            </li>
          ))}
        </ol>
        {state.error !== null ? (
          <p className="mt-3 text-sm text-red-700" role="alert">
            {state.error}
          </p>
        ) : null}
        <button
          type="button"
          onClick={() => {
            void onSubmit();
          }}
          disabled={!allAnswered || state.submitting}
          className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
        >
          {state.submitting ? "Grading…" : "Submit"}
        </button>
      </section>
    );
  }

  // state.kind === "reviewing"
  const { response } = state;
  return (
    <section
      className={`rounded-lg border p-6 ${
        response.passed
          ? "border-emerald-200 bg-emerald-50"
          : "border-amber-200 bg-amber-50"
      }`}
    >
      <h2 className="text-lg font-semibold text-foreground">
        {response.passed
          ? `Passed — ${response.score}%`
          : `Not quite — ${response.score}%`}
      </h2>
      <p className="mt-2 text-sm text-foreground">
        Threshold: {response.passing_percent}%. Review each question below;
        rationales explain why the right answer is right.
      </p>
      <ol className="mt-5 space-y-6">
        {response.items.map((item, idx) => (
          <li key={item.id} className="rounded-md bg-white p-4">
            <p className="text-sm font-semibold text-foreground">
              {idx + 1}. {item.stem}
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              {item.options.map((opt) => {
                const isCandidate = opt.id === item.candidate_option_id;
                const isCorrect = opt.id === item.correct_option_id;
                return (
                  <li
                    key={opt.id}
                    className={`rounded px-2 py-1 ${
                      isCorrect
                        ? "bg-emerald-100 font-semibold text-emerald-900"
                        : isCandidate
                          ? "bg-red-100 text-red-900"
                          : "text-foreground"
                    }`}
                  >
                    <span className="font-mono text-xs uppercase text-muted-foreground">
                      {opt.id}.
                    </span>{" "}
                    {opt.text}
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-sm text-foreground">
              <span className="font-semibold">Why:</span>{" "}
              {
                item.options.find((opt) => opt.id === item.correct_option_id)
                  ?.rationale
              }
            </p>
          </li>
        ))}
      </ol>
      <button
        type="button"
        onClick={onContinue}
        className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
      >
        {response.passed ? "Continue" : "Try again"}
      </button>
    </section>
  );
}
